/**
 * MemberCounter — "Road to 200" tracker.
 *
 * Big card on the Coach Dashboard hero. Shows live count, target (200),
 * progress bar, weekly delta, and an expandable roster of the athletes
 * who currently meet BOTH conditions (paying sub + active recurring
 * template). See /api/coach/members/count.
 *
 * Sachin spec (2026-02-13): "I want to know how many people are members
 * who are paying a weekly membership."
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Loader2, TrendingUp, TrendingDown, Minus, ChevronDown, Trophy, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MemberCounter() {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);
    // When set, the expanded roster panel filters to "unscheduled only" — the
    // mode the CTA jumps into. Toggling the regular "Show roster" button
    // returns to the full roster.
    const [showUnscheduled, setShowUnscheduled] = useState(false);

    useEffect(() => {
        let alive = true;
        axios.get(`${API}/coach/members/count`, { withCredentials: true })
            .then((r) => { if (alive) setData(r.data); })
            .catch(() => { if (alive) setData({ error: true }); });
        return () => { alive = false; };
    }, []);

    if (!data) {
        return (
            <div className="border border-zinc-900 bg-zinc-950 px-5 py-6 flex items-center gap-2 text-zinc-400 text-sm" data-testid="member-counter-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading member count…
            </div>
        );
    }
    if (data.error) {
        return null;
    }

    const count = data.count || 0;
    const target = data.target || 200;
    const pct = Math.min(100, Math.round((count / target) * 100));
    const delta = data.delta_wow || 0;

    return (
        <div
            data-testid="member-counter-card"
            className="border border-zinc-900 bg-zinc-950 overflow-hidden"
        >
            <div className="px-5 py-5 md:py-6 flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
                {/* Left — the big number */}
                <div className="md:flex-1">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red inline-flex items-center gap-2">
                        <Trophy className="w-3 h-3" /> Road to 200
                    </div>
                    <div className="mt-2 flex items-baseline gap-3">
                        <div
                            data-testid="member-counter-count"
                            className="font-display text-6xl md:text-7xl tracking-wider leading-none text-white"
                        >
                            {count}
                        </div>
                        <div className="text-xl text-zinc-500 tracking-wide">/ {target}</div>
                    </div>
                    <div className="mt-2 text-[11px] tracking-[0.2em] uppercase text-zinc-500">
                        Paying members
                    </div>
                    {/* Scheduling gap CTA — paying athletes who aren't on the
                        standing recurring schedule yet. Sachin's "click to fix"
                        path: tapping it expands the unscheduled list with deep
                        links into each athlete's dossier where the coach can
                        add a recurring template. (Sachin 2026-02-16 — replaces
                        the strict dual-gate that was hiding real members.) */}
                    {data.unscheduled_count > 0 && (
                        <button
                            type="button"
                            data-testid="member-counter-unscheduled-cta"
                            onClick={() => { setOpen(true); setShowUnscheduled(true); }}
                            className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-amber-300 hover:text-amber-200 transition-colors text-left"
                        >
                            <AlertCircle className="w-3 h-3 flex-shrink-0" />
                            <span>
                                <span className="font-semibold">{data.unscheduled_count}</span>
                                {" not on the standing schedule — click to fix"}
                            </span>
                        </button>
                    )}
                </div>

                {/* Middle — progress bar */}
                <div className="md:flex-[2] w-full">
                    <div className="flex items-center justify-between text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2">
                        <span>{pct}% there</span>
                        <span>{target - count} to go</span>
                    </div>
                    <div className="w-full h-3 bg-black border border-zinc-800 overflow-hidden">
                        <div
                            data-testid="member-counter-bar"
                            className="h-full bg-brand-red transition-all duration-700"
                            style={{ width: `${pct}%` }}
                        />
                    </div>
                    <div className="mt-3 flex items-center justify-between text-xs">
                        <DeltaPill delta={delta} />
                        <button
                            type="button"
                            data-testid="member-counter-toggle-roster"
                            onClick={() => setOpen((o) => !o)}
                            className="text-[11px] tracking-[0.18em] uppercase text-zinc-300 hover:text-brand-red inline-flex items-center gap-1"
                        >
                            {open ? "Hide roster" : "Show roster"} <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
                        </button>
                    </div>
                </div>
            </div>

            {open && (
                <div data-testid="member-counter-roster" className="border-t border-zinc-900 bg-black/50 px-5 py-4">
                    {data.athletes?.length === 0 ? (
                        <div className="text-sm text-zinc-400">No paying members yet.</div>
                    ) : (
                        <>
                            {/* Mode toggle — when there are unscheduled folks we
                                let the coach flip between the full roster and
                                the "needs a recurring template" subset so they
                                can fix the gap quickly. */}
                            {data.unscheduled_count > 0 && (
                                <div className="flex items-center gap-1 mb-3 text-[10px] tracking-[0.2em] uppercase">
                                    <button
                                        type="button"
                                        data-testid="member-counter-mode-all"
                                        onClick={() => setShowUnscheduled(false)}
                                        className={`px-2 py-1 border transition-colors ${
                                            showUnscheduled
                                                ? "border-zinc-800 text-zinc-400 hover:text-white"
                                                : "border-brand-red text-brand-red"
                                        }`}
                                    >
                                        All ({data.count})
                                    </button>
                                    <button
                                        type="button"
                                        data-testid="member-counter-mode-unscheduled"
                                        onClick={() => setShowUnscheduled(true)}
                                        className={`px-2 py-1 border transition-colors inline-flex items-center gap-1 ${
                                            showUnscheduled
                                                ? "border-amber-500 text-amber-300"
                                                : "border-zinc-800 text-zinc-400 hover:text-amber-300"
                                        }`}
                                    >
                                        <AlertCircle className="w-3 h-3" /> Needs schedule ({data.unscheduled_count})
                                    </button>
                                </div>
                            )}
                            <ul
                                data-testid={showUnscheduled ? "member-counter-unscheduled-list" : "member-counter-all-list"}
                                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2"
                            >
                                {(showUnscheduled ? (data.unscheduled || []) : (data.athletes || [])).map((a) => (
                                    <li
                                        key={a.athlete_id}
                                        className={`text-sm border bg-zinc-950 px-3 py-2 flex flex-col ${
                                            !showUnscheduled && a.on_standing_schedule === false
                                                ? "border-amber-900/60"
                                                : "border-zinc-900"
                                        }`}
                                    >
                                        <Link
                                            to={`/coach/clients/${a.parent_user_id}`}
                                            data-testid={`member-counter-athlete-${a.athlete_id}`}
                                            className="text-white hover:text-brand-red transition-colors truncate inline-flex items-center gap-1.5"
                                        >
                                            {a.first_name} {a.last_name}
                                            {!showUnscheduled && a.on_standing_schedule === false && (
                                                <AlertCircle className="w-3 h-3 text-amber-400 flex-shrink-0" title="Not on the standing schedule" />
                                            )}
                                        </Link>
                                        <span className="text-[11px] text-zinc-500 truncate">{a.parent_name}</span>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

function DeltaPill({ delta }) {
    if (delta === 0) {
        return (
            <span className="inline-flex items-center gap-1 text-[11px] text-zinc-500" data-testid="member-counter-delta">
                <Minus className="w-3 h-3" /> No change vs. last week
            </span>
        );
    }
    const positive = delta > 0;
    const Icon = positive ? TrendingUp : TrendingDown;
    return (
        <span
            data-testid="member-counter-delta"
            className={`inline-flex items-center gap-1 text-[11px] font-semibold ${positive ? "text-emerald-400" : "text-rose-400"}`}
        >
            <Icon className="w-3 h-3" /> {positive ? "+" : ""}{delta} this week
        </span>
    );
}

/**
 * Compact badge for the sidebar — "128 / 200" mini progress dot.
 */
export function MemberCounterBadge({ onClick = null }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        let alive = true;
        axios.get(`${API}/coach/members/count`, { withCredentials: true })
            .then((r) => { if (alive) setData(r.data); })
            .catch(() => {});
        return () => { alive = false; };
    }, []);
    if (!data || data.error) return null;
    const pct = Math.min(100, Math.round((data.count / data.target) * 100));
    return (
        <Link
            to="/coach"
            data-testid="member-counter-badge"
            title={`${data.count} of ${data.target} members — ${pct}%`}
            onClick={onClick || undefined}
            className="block px-3 pt-2 pb-1 hover:bg-zinc-900 transition-colors"
        >
            <div className="flex items-center justify-between text-[9px] tracking-[0.22em] uppercase text-zinc-500">
                <span className="inline-flex items-center gap-1">
                    <Trophy className="w-3 h-3 text-brand-red" /> Members
                </span>
                <span className="text-zinc-300 tracking-normal text-[11px] font-mono">
                    <span className="text-white font-semibold">{data.count}</span>/{data.target}
                </span>
            </div>
            <div className="mt-1 w-full h-1 bg-black border border-zinc-800 overflow-hidden">
                <div className="h-full bg-brand-red" style={{ width: `${pct}%` }} />
            </div>
        </Link>
    );
}
